import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormHelperText, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Snackbar, TextField } from "@mui/material"
import { DatePicker, DateTimePicker, LocalizationProvider, TimePicker, TimeView } from "@mui/x-date-pickers"
import { FormProps, FormPropsField, Redemption } from '../../types/props'
import { useLanguage, useRecipient } from '../../contexts/RecipientContext'
import { getAPIOPtions, getRgbaParts, sendRedemption, submitForm } from '../../services/HelperService'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { Survey } from '../../types/form'
import { SelectLoader } from '../SelectLoader/SelectLoader'
import { InputMask } from "@react-input/mask"
import { DateTime } from 'luxon'
import { us_states } from '../../data/us.states'
import * as _ from 'lodash'

import './FormRenderer.scss'

const VEHICLE_FIELD_MAP: {[key: string]: string} = {
	make: 'auto_make',
	year: 'auto_year',
	model: 'auto_model',
	trim: 'auto_trim'
}

export const FormRenderer = ({survey_init, form, step = null, onStepUpdated, onSurveyUpdated, popup = true}: {survey_init?: any, popup?: boolean, form: FormProps, step?: number|null, onStepUpdated: (step: number) => void, onSurveyUpdated?: (survey: any) => void}) => {
	const [errors, setErrors] = useState<{[key: string]: string}>({})
	const [model, setModel] = useState<{[key: string]: any}>({})
	const {recipient, token, setRecipient} = useRecipient()
	const [mode, setMode] = useState<string>('form')
	const [submitting, setSubmitting] = useState<boolean>(false)
	const [survey, setSurvey] = useState<Survey>(null)
	const [surveyAnswer, setSurveyAnswer] = useState<any>(null)
	const [surveyError, setSurveyError] = useState<any>(null)
	const [loading, setLoading] = useState<boolean>(true)
	const [automotiveMake, setAutomotiveMake] = useState<any[]>([])
	const [automotiveModel, setAutomotiveModel] = useState<any[]>([])
	const [automotiveYear, setAutomotiveYear] = useState<any[]>([])
	const [automotiveTrim, setAutomotiveTrim] = useState<any[]>([])
	const [optionsLoading, setOptionsLoading] = useState<{[key: string]: boolean}>({})
	const [emailSuccess, setEmailSuccess] = useState<boolean>(false)
	const [smsSuccess, setSmsSuccess] = useState<boolean>(false)
	const {variables}: any = useRecipient()
	const {language}: { language: 'US'|'ES'|'FR'} = useLanguage()

	const fieldStyle = {
		display: 'block',
		width: '100%', 
		marginBottom: '10px',
		borderRadius: form.fieldStyle.borderRadius,
		'.MuiInputBase-root': {
			width: '100%',
			borderRadius: form.fieldStyle.borderRadius,
			backgroundColor: form.fieldStyle.backgroundColor,
		},
		'.MuiOutlinedInput-notchedOutline': {
			borderColor: `${form.fieldStyle.borderColor}!important`
		},
		'.MuiInputLabel-root.Mui-focused:not(.Mui-error)': {
			color: `${form.fieldStyle.borderColor}`
		},
		'.MuiFilledInput-underline:after': {
			borderBottomColor: `${form.fieldStyle.borderColor}!important`
		},
		'.MuiFilledInput-underline:before': {
			borderBottomColor: `${form.fieldStyle.borderColor}!important`
		},
		'.Mui-error': {
			'&.MuiFormHelperText-root': {
				marginTop: 0,
				position: 'absolute',
				bottom: '-2px',
				transform: 'translateY(100%)',
				fontSize: '10px'
			},
			'.MuiOutlinedInput-notchedOutline': {
				borderColor: `#d32f2f!important`
			},
			'.MuiFilledInput-underline:after': {
				borderBottomColor: `#d32f2f!important`
			},
			'.MuiFilledInput-underline:before': {
				borderBottomColor: `#d32f2f!important`
			},
		}
	}
	let button_rgba: RegExpMatchArray | null = form.button.style.backgroundColor ? getRgbaParts(form.button.style.backgroundColor) : null
	let button_hover: string

	if (button_rgba) {
		button_hover = `rgba(${button_rgba[1]},${button_rgba[2]},${button_rgba[3]},${parseInt(button_rgba[4]) - 0.1})`
	}

	const confirmationDescription: any = form.confirmation?.description
	const hasConfirmationDescription = !!(confirmationDescription?.text || confirmationDescription?.[language]?.text) && (confirmationDescription?.enabled ?? true)
	const confirmationColor = (hasConfirmationDescription && confirmationDescription?.style?.color) || form.confirmation?.title?.style?.color

	useEffect(() => {
		loadOptions('automotiveMake')
		loadOptions('automotiveYear')
	}, [])

	useEffect(() => {
		if (survey_init) {
			setMode('survey')
			setSurvey(survey_init)
		}
	}, [survey_init])

	useEffect(() => {
		if (model['make']) {
			// Reset previous values
			setAutomotiveYear([])
			setAutomotiveModel([])
			setAutomotiveTrim([])

			// Load new values
			loadOptions('automotiveYear')
			loadOptions('automotiveModel')
		}
	}, [model.make])

	useEffect(() => {
		if (model['make'] && model['year']) {
			// Reset previous values
			setAutomotiveModel([])
			setAutomotiveTrim([])

			// Load new values
			loadOptions('automotiveModel')
		}
	}, [model.year])

	useEffect(() => {
		if (model['model']) {
			// Reset previous values
			setAutomotiveTrim([])

			// Load new values
			loadOptions('automotiveTrim')
		}
	}, [model.model])

	const getVehicleModel = () => {
		const vehicle = variables?.vehicle
		let vehicle_model: {[key: string]: any} = {}

		if (!vehicle) return vehicle_model

		Object.entries(VEHICLE_FIELD_MAP).forEach(([key, source]) => {
			if (vehicle[source] !== null && vehicle[source] !== undefined && vehicle[source] !== '') {
				vehicle_model[key] = vehicle[source]
			}
		})

		return vehicle_model
	}

	useEffect(() => {
		if (recipient) {
			setModel({...recipient, ...getVehicleModel()})

			if (recipient.initialOptions?.content_type === 'POLL') {
				if (!popup) {
					onSurveyUpdated(recipient.initialOptions)
				} else {
					setSurvey(recipient.initialOptions)
					setMode('survey')
				}
			}
			
			setLoading(false)
		}
	}, [recipient])

	const loadOptions = (type: string) => {
		setOptionsLoading(current => ({...current, [type]: true}))
		
		getAPIOPtions(type, model).then(response => {
			if (!response.data.error) {
				const newOptions = response.data.data

				switch (type) {
					case 'automotiveMake':
						setAutomotiveMake(newOptions)
						break
					case 'automotiveModel':
						setAutomotiveModel(newOptions)
						break
					case 'automotiveTrim':
						setAutomotiveTrim(newOptions)
						break
					case 'automotiveYear':
						setAutomotiveYear(newOptions)
						break
				}
			}
		}).finally(() => {
			setOptionsLoading(current => ({...current, [type]: false}))
		})
	} 

	const replaceVariables = (text: string) => {
		return text.replaceAll(/({{)(.*?)(}})/g, (match: string, offset: number) => {
			const key = match.substring(2, match.length - 2)

			return variables[key] || ''
		})
	}


	const checkValue = (type: string, value: any, required: boolean, label: string): any => {  
		if (!value) {
			if (required) {
				return `${label} is required.`
			}
		} else {
			let valid = true
			switch (type) {
				case 'email':
					valid = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value)
					
					if (!valid) {
						return 'Please enter a valid email address.'
					}
					break
				case 'phone':
					const regex = /^\+1 \(\d{3}\) \d{3}-\d{4}$/
					valid = regex.test(value)

					if (!valid) {
						return 'Please enter a valid phone number.'
					}
					break
			}
		}
		
		return null
	}

	const checkFormValue = (field: FormPropsField, new_model?: any): any => {
		let new_errors: {[key: string]: string} = {...errors}
		const used_model = new_model ? new_model : model

		const error = checkValue(field.type, used_model[field.key], field.required, field.label)
		
		if (error) {
			new_errors[field.key] = error
		} else if (errors[field.key]) {
			delete new_errors[field.key]
		}

		setErrors(new_errors)
		
		return new_errors
	}

	const checkSurveyValue = (type: string, event: string): string | null => {
		const error = checkValue(type, surveyAnswer, true, 'Answer')

		if (surveyError || event !== 'change') {
			setSurveyError(error)
		}

		return error
	}

	const onModelChange = (field: FormPropsField, event: any) => {
		let new_model = {...model}

		if (field.type === 'opt-in') {
			new_model[field.key] = event.target.checked
		} else {
			new_model[field.key] = event.target.value
		}

		setModel(new_model)
		// re-validate the error
		if (errors[field.key]) {
			checkFormValue(field, new_model)
		}
	}
	
	const setRedemption = (redemption: Redemption | null) => {
		if (redemption) {
			setRecipient({...recipient,
				redemption: redemption
			})
		}
	}

	const confirmationStep = () => {
		const confirm = form.confirmation
		if (confirm.external_link && confirm.link?.url && confirm.link?.type === 'redirect') {
			window.open(confirm.link.url)
		}

		if (survey_init) {
			onSurveyUpdated(null)
		}

		// -1 is the thank you page
		onStepUpdated(-1)
	}

	const onSubmitForm = () => {
		const last_step = step + 1 === form.fields.length
		
		if (!token) {
			// preview mode
			if (!last_step) {
				onStepUpdated(step + 1)
			} else {
				confirmationStep()
			}
		} else {
			setSubmitting(true)

			if (mode === 'survey') {
				let error = null
	
				if (survey.question) {
					error = checkSurveyValue(survey.question.type, 'submit')
				}
	
				if (error) {
					setSubmitting(false)
				} else {
					let request: any
		
					if (survey.content_type === 'APPT') {
						request = {
							token, 
							time: surveyAnswer
						}
					} else if (survey.question?.type === 'Multiple Choice') {
						request = {
							token, 
							answer: surveyAnswer,
							question: survey.question.id
						}
					} else {
						request = {
							formAnswer: surveyAnswer,
							token
						}
					}

					request.last_step = last_step
					request.step = step + 1
		
					submitForm(request).then((response) => {
						if (['POLL', 'APPT'].includes(response.data.content_type)) {
							setSurvey(response.data)
						} else if (response.data.content_type === 'CONFIRM') {
							setRedemption(response.data.redemption)
							confirmationStep()
						}
	
						setSurveyAnswer(null)
						setSubmitting(false)
					})
				}
			} else {
				const last_step = step + 1 === form.fields.length
				let submit_errors: any = {}
				let model_to_submit: any = {}
				let can_submit: boolean = true
		
				form.fields[step]?.forEach(group => {
					group.forEach(field => {
						model_to_submit[field.key] = model[field.key]
						submit_errors = {...submit_errors, ...checkFormValue(field)}
						if (submit_errors[field.key]) can_submit = false
					})
				})
		
				setErrors(submit_errors)
		
				if (can_submit) {
					submitForm({
						...model_to_submit,
						token,
						form_completed: last_step,
						form_step: step+1
					}).then((response: any) => {
						if (!last_step) {
							onStepUpdated(step + 1)
						} else {
							// Check reponse for whether or not the survey is enabled.
							if (['POLL', 'APPT'].includes(response?.data?.content_type)) {
								if (!popup) {
									onSurveyUpdated(response.data)
								} else {
									setSurvey(response.data)
									setMode('survey')
								}
							} else {
								setRedemption(response.data.redemption)
								confirmationStep()
							}
						}
		
						setSubmitting(false)
					})
				} else {
					setSubmitting(false)
				}
			}
		}
 
	}

	const getTextField = (label: string, required: boolean, type: string, extraSettings: any, floatingLabel: boolean = true) => {
		switch(type) {
			case 'number': 
				extraSettings = {
					...extraSettings,
					type: 'number'
				}
				break
		}

		return <TextField
			sx={{
				...fieldStyle,
				'input': {
					borderRadius: 'inherit'
				},
				'& .MuiInputLabel-root.MuiInputLabel-shrink': {
					backgroundColor: '#fff',
					px: 2,
					padding: '0 12px',
					borderRadius: 5
				},
			}}
			InputProps={{
				notched: false,
			}}
			FormHelperTextProps={{
				margin: 'dense',
				required: true
			}}
			size={form.fieldStyle.size}
			label={form.fieldStyle.floatingLabel && floatingLabel ? label: ''}
			placeholder={label}
			required={required}
			variant={form.fieldStyle?.fieldType} 
			{ ...extraSettings }/>
	}

	const getDateField = (label: string, extraSettings: any) => {
		return <DatePicker sx={{
				...fieldStyle
			}}
			slotProps={{ textField: {
				size: form.fieldStyle.size
			}}}
			label={label}
			{...extraSettings}/>
	}

	const getSelectField = (label: string, key: string, required: boolean, extraSettings: any, options: any[], error: string, loading: boolean = false) => {
		return <FormControl key={key}
			sx={{
				...fieldStyle,
				'& .MuiInputLabel-root.MuiInputLabel-shrink': {
					backgroundColor: '#fff',
					px: 2,
					padding: '0 12px',
					borderRadius: 5
				},
			}}
			error={!!error}
			required={required}
			size={form.fieldStyle.size}
			fullWidth>
			<InputLabel id={key}>
				{ label }
			</InputLabel>
			<Select labelId={key}
				label={ label }
				variant={form.fieldStyle.fieldType}
				// helperText={error}
				notched={false}
				sx={{
					backgroundColor: `${form.fieldStyle.backgroundColor}!important`
				}}
				IconComponent={loading ? SelectLoader : undefined}
				disabled={loading}
				{ ...extraSettings }>
				{ options }
			</Select>
			{error && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	}

	const getRadioField = (label: string, options: any[], extraSettings: any) => {
		return <FormControl>
			{ label && <FormLabel id={label}>
				{ label }
			</FormLabel> }
			<RadioGroup {...extraSettings}
				sx={{
					'.MuiButtonBase-root': {
						color: form.fieldStyle.borderColor,
						'&.Mui-checked': {
							color: form.fieldStyle.borderColor
						}
					}
				}}
				aria-labelledby={label}>
				{ options }
			</RadioGroup>
		</FormControl>
	}

	const getAppointmentField = () => {
		const shouldDisableDate = (day: DateTime) => {
			let day_of_week = day.toFormat('cccc').toLowerCase()
			const hours: any = survey.event_hours
			return hours[`${day_of_week}_from`] === hours[`${day_of_week}_to`]
		}

		const shouldDisableTime = (value: DateTime, view: TimeView) => {
			if (view === 'hours') {
				let day_of_week = value.toFormat('cccc').toLowerCase()
				const hour = parseInt(value.toFormat('HH'))
				const hours: any = survey.event_hours

				return hour < parseInt(hours[`${day_of_week}_from`]) || hour > parseInt(hours[`${day_of_week}_to`])
			} 
			
			return false
		}

		return <DateTimePicker sx={{...fieldStyle}}
			disablePast={true}
			maxDate={DateTime.fromFormat(survey.end_date, 'yyyy-MM-dd')}
			onChange={(newValue: DateTime) => {setSurveyAnswer(newValue.toISO())}}
			shouldDisableDate={shouldDisableDate}
			shouldDisableTime={shouldDisableTime}
			disableIgnoringDatePartForTimeValidation={true}
		/>
	}

	const getTimeField = (label: string, floatingLabel: boolean) => {
		return <TimePicker sx={{
				...fieldStyle
			}}
			slotProps={{ textField: {
				size: form.fieldStyle.size
			}}}
			label={floatingLabel ? label : ''}
			onChange={(newValue: DateTime) => {setSurveyAnswer(newValue.toFormat('HH:mm'))}}
		/>
	}

	const getField = (field: FormPropsField, index: number) => {
		const label = getLanguageValue(field, '', 'label')	
		let extraSettings: any = {
			key: index
		}

		switch(field.type) {
			case 'number': 
				extraSettings = {
					type: 'number',
					notched: false,
				}
				break
			case 'phone':
				extraSettings = {
					InputProps: {
						inputComponent: InputMaskComponent,
						notched: false,
					}
				}
				break
		}

		switch(field.type) {
			case 'phone': 
			case 'text': 
			case 'number':
			case 'email': 
				extraSettings = {
					...extraSettings,
					error: errors[field.key] !== undefined,
					value: model[field.key] || '',
					helperText: errors[field.key],
					onChange: (event: any) => onModelChange(field, event),
					onBlur: (event: any) => checkFormValue(field),
				}			

				return getTextField(label, field.required, field.type, extraSettings)
			case 'select':

				let raw_options: {
					value: string
					label: string
					[key: string]: any // allow extra dynamic keys
				}[] = []

				if (field.loadOptions) {
					const type: string = field.loadOptions
					switch (type) {
						case 'automotiveMake':
							raw_options = automotiveMake
							break
						case 'automotiveModel':
							raw_options = automotiveModel
							break
						case 'automotiveTrim':
							raw_options = automotiveTrim
							break
						case 'automotiveYear':
							raw_options = automotiveYear
							break
					}
				} else {
					raw_options = field.options
				}

				const current = model[field.key] ?? ''
				const matched = raw_options.find(option => String(option.value) === String(current))

				const options = raw_options.map(option => {
					let label = option[language]?.label || option.label

					return <MenuItem key={option.value} value={option.value}>{label}</MenuItem>
				})

				const selectSettings = {
					value: matched?.value ?? current,
					onClose: (event: any) => { 
						if (event.target.value === undefined) {
							checkFormValue(field)
						}
					},
					onChange: (event: any) => onModelChange(field, event)
				}

				return getSelectField(label, index.toString(), field.required, selectSettings, options, errors[field.key], !!optionsLoading[field.loadOptions])
			case 'date':
				return getDateField(label, extraSettings)
			case 'opt-in': 
				if (model[field.key] === undefined && field.pre_check) {
					let new_model = {...model}
					new_model[field.key] = true
			
					setModel(new_model)
				}

				const getTermsAndPrivacy = (field: FormPropsField) => {
					if (field.terms) {
						if (field.privacy) {
							return <> View <a href={field.terms} target="_blank">Terms of Conditions</a> and <a href={field.privacy} target="_blank">Privacy Policy</a>.</>
						} else {
							return <> View <a href={field.terms} target="_blank">Terms of Conditions</a>.</>
						}
					} else {
						if (field.privacy) {
							return <> View <a href={field.privacy} target="_blank">Privacy Policy</a>.</>
						} else {
							return <></>
						}
					}
				}

				return  <FormControlLabel key={index}
					className="dm-edit-form-optin"
					control={
						<Checkbox checked={model[field.key] || false}
							sx={{
								color: field.color,
								'&.Mui-checked': {
									color: field.color,
								}
							}}
							onChange={(event: any) => onModelChange(field, event)} 
							name={field.label} />
					}
					label={
						<div style={{fontSize: field.fontSize, lineHeight: 1.2, color: field.color as any}}>
							{getLanguageValue(field, '', 'message')} 
							{getTermsAndPrivacy(field)}
						</div>
					}
				/>
		}
	}

	const getSurveyField = () => {
		if (survey.content_type === 'APPT') {
			return getAppointmentField()
		} 

		let extraSettings: any = {
			value: surveyAnswer || '',
			onChange: (event: any) => { 
				setSurveyAnswer(event.target.value)
				checkSurveyValue(type, 'change')
			}
		}

		let type = 'text'
		const label = ''
		// Logic that renders fields (see getField as an example)
		switch (survey.question.type) {
			case 'Multiple Choice':
				const options = survey.answers.map(answer => <MenuItem key={answer.id} value={answer.id}>{answer.text}</MenuItem>)
				return getSelectField(label, survey.question.id.toString(), true, extraSettings, options, null)
			case 'Yes/No':
				const radioOptions = [{value: 'yes', label: 'Yes'}, {value: 'no', label: 'No'}].map(option => <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />)
				return getRadioField('', radioOptions, extraSettings)
			case 'Date':
				const dateExtraSettings = {
					onChange: (event: DateTime) => {
						setSurveyAnswer(event.toFormat('yyyy-MM-dd')) 
					}
				}
				return getDateField(label, dateExtraSettings) 
			case 'Time':
				return getTimeField(label, false)
			case 'Appointment':
			case 'Month':
				const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
				const monthOptions = months.map(month => <MenuItem key={month} value={month}>{month}</MenuItem>)
				return getSelectField(label, survey.question.id.toString(), true, extraSettings, monthOptions, null)
			case 'Day Of Week':
				const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
				const weekdayOptions = weekdays.map(weekday => <MenuItem key={weekday} value={weekday}>{weekday}</MenuItem>)
				return getSelectField(label, survey.question.id.toString(), true, extraSettings, weekdayOptions, null)
				break
			case 'US State':
				const stateOption = us_states.map(state => <MenuItem key={state.value} value={state.label}>{state.label}</MenuItem>)
				return getSelectField(label, survey.question.id.toString(), true, extraSettings, stateOption, null)
			case 'Phone Number':
			case 'City':
			case 'Email':
			case 'Number':
			case 'AlphaNumeric':
				switch (survey.question.type) {
					case 'Number':
						type = 'number'
						break
					case 'Email':
						type = 'email'
						break
					case 'Phone Number':
						type = 'phone'

						extraSettings = {
							...extraSettings,
							InputProps: {
								inputComponent: InputMaskComponent
							}
						}
						break
				}

				extraSettings = {
					...extraSettings,
					onBlur: (event: any) => checkSurveyValue(type, 'blur'),
					error: surveyError ? true : false,
					helperText: surveyError
				}

				return getTextField(label, true, type, extraSettings, false)
		}
	}

	const getLanguageValue = (parent: any, first_path: string, second_path: string) => {
		if (first_path === 'header_image') {
			const default_path = 'header_image.url'

			if (parent.header_image.language_specific) {
				return _.get(parent, `header_image.${language}.url`, _.get(parent, default_path)) 
			}

			return  _.get(parent, default_path)
		}

		const first_part = first_path ? `${first_path}.` : ''
		const en_path = `${first_part}${second_path}`
		const lang_path = `${first_part}${language}.${second_path}`

		const value = _.get(parent, lang_path, _.get(parent, en_path))
		
		if (value.includes('{{')) {
			return replaceVariables(value || '')
		} 

		return value
	}
	const RedemptionButton = (type: 'print'|'email'|'text') => {
		const onSendRedemption = () => {
			if (type === 'print') {
				window.print()
			} else {
				sendRedemption(type, token).then((response) => {
					if (type === 'email') {
						setSmsSuccess(false)
						setEmailSuccess(true)
					} else {
						setEmailSuccess(false)
						setSmsSuccess(true)
					}
				})
			}
		}

		const labels = {
			print: 'Print Offer',
			email: 'Email Offer',
			text: 'Text Offer'
		}

		return <div className='redemption__button__holder'>
				<Button sx={{
					display: 'block', 
					marginBottom: '10px',
					textTransform: 'none', 
					...form.button.style,
					'&:hover': {
						backgroundColor: button_rgba ? button_hover : 'inherit'
					},
					'&.Mui-disabled': {
						...form.button.style
					}
				}}
				disabled={submitting}
				onClick={onSendRedemption}>
				{ labels[type] }
			</Button>
		</div>
	}

	useEffect(() => {
		// if redemption was already happened it should always show redemption no matter what @Ryan requested
		if(recipient?.initialOptions?.redemption) {
			setRedemption(recipient?.initialOptions?.redemption)
		}
	}, [])

	return loading ? <></> :
		<>
			{ form.header_image?.url &&
				<img src={getLanguageValue(form, 'header_image', '')} 
					style={{
						width: '100%',
						height: 'auto'
					}}/>
			}
			{ (step === -1 || survey?.content_type === 'CONFIRM') ? 
				<div className="dm-edit-form__confirmation">
					{(form.confirmation.title?.enabled ?? true) && <h3 style={form.confirmation.title.style}>
						{ getLanguageValue(form, 'confirmation.title', 'text') }
					</h3>}
					{ (form.confirmation.description?.text || (form.confirmation.description as any)?.[language]?.text) && (form.confirmation.description?.enabled ?? true) &&
						<p style={form.confirmation.description.style}>
							{ getLanguageValue(form, 'confirmation.description', 'text') }
						</p>
					}
					{ form.confirmation.external_link &&
						form.confirmation.link?.url &&
						form.confirmation.link?.type === 'button' &&
						<Button sx={{display: 'block', 
							textTransform: 'none', 
							textAlign: 'center',
							...form.confirmation.link.style}}
							href={form.confirmation.link.url}>
							{ getLanguageValue(form, 'confirmation.link', 'label') }
						</Button>
					}
					{ recipient.redemption &&
						<div id="dm-edit-print-area">
							<h3 style={form.confirmation.title.style}>
								{ recipient.redemption.text_header }
							</h3>
							{ recipient.redemption.text_content &&
								<p style={form.confirmation.description.style}>
									{ recipient.redemption.text_content }
								</p>
							}
							{
								recipient.redemption.qrcode_enable &&
								<img width="100"
									src={recipient.redemption.code_image} />
							}
							{
								recipient.redemption.image_enabled &&
								<img width="300"
									src={recipient.redemption.redeem_image} />
							}
							{ recipient.redemption.expiration &&
								<p style={form.confirmation.description.style}>
									Expires on: { recipient.redemption.expiration }
								</p>
							}
							{ recipient.redemption.text_disclaimer &&
								<p style={form.confirmation.description.style}>
									{ recipient.redemption.text_disclaimer }
								</p>
							}
							{
								recipient.redemption.print_enabled &&
								RedemptionButton('print')
							}
							{
								recipient.redemption.email_enabled &&
								<>
									{ RedemptionButton('email') }
									<Snackbar
										open={emailSuccess}
										autoHideDuration={60000}
										onClose={() => setEmailSuccess(false)}
										sx={{'.MuiPaper-root:not(.MuiAlert-root)': {padding: 0}, '.MuiSnackbarContent-message': {padding: 0}}}
										message={<Alert sx={{padding: '4px 10px'}} variant="filled" severity="success">
												Redemption information was sent via email.
											</Alert>}
									/>
								</>
							}
							{
								recipient.redemption.sms_enabled &&
								<>
									{ RedemptionButton('text') }
									<Snackbar
										open={smsSuccess}
										autoHideDuration={60000}
										onClose={() => setSmsSuccess(false)}
										sx={{'.MuiPaper-root:not(.MuiAlert-root)': {padding: 0}, '.MuiSnackbarContent-message': {padding: 0}}}
										message={<Alert sx={{padding: '4px 10px'}} variant="filled" severity="success">
												Redemption information was sent via text message.
											</Alert>}
									/>
								</>
							}
						</div>
					}
				</div> : 
				<LocalizationProvider dateAdapter={AdapterLuxon}>
					<form style={{
						minWidth: '300px',
						display: 'flex',
						flexDirection: 'column',
						gap: form.marginY
					}}>
						{ mode === 'form' ? 
							<>
								{ form.label.text && (form.label?.enabled ?? true) && 
									<h2 style={{
										margin: 0,
										textAlign: form.top_alignment,
										...form.label.style
									}}>
									{ getLanguageValue(form, 'label', 'text') }
								</h2> 
								}
								{ form.description.text && (form.description?.enabled ?? true) && 
									<p style={{
										margin: 0,
										lineHeight: 1.5,
										textAlign: form.top_alignment,
										...form.description.style
									}}>
									{ getLanguageValue(form, 'description', 'text') }
								</p> 
								}
								{	form.fields?.[step] &&
									form.fields[step].map((group, index) => 
										<div key={index}
											className="dm-edit-form__group"
											style={{gap: form.marginX}}>
											{ group.map((field, field_index) => getField(field, field_index)) }
										</div>)
								}
							</> :
							<>
								{/* Here goes survey logic */}
								<h2 style={{
										margin: 0,
										textAlign: form.top_alignment,
										...form.label.style
									}}>
									{ survey.text_header }
								</h2> 
								<h4 style={{
									margin: 0,
									...form.description.style            
								}}>
									{ survey.question?.text }
								</h4>
								<Box sx={confirmationColor ? {
									'&& .MuiFormControlLabel-label': {
										color: confirmationColor
									}
								} : {}}>
									{ getSurveyField() }
								</Box>
							</>
						}
						<Button sx={{
								display: 'block', 
								textTransform: 'none', 
								...form.button.style,
								'&:hover': {
									backgroundColor: button_rgba ? button_hover : 'inherit'
								},
								'&.Mui-disabled': {
									...form.button.style
								}
							}}
							disabled={submitting}
							onClick={onSubmitForm}>
							{
								submitting ? <CircularProgress style={{verticalAlign: 'middle'}} 
									size="16px" 
									color="inherit" /> : mode === 'survey' ? survey.submit_text : getLanguageValue(form, 'button', 'label')
							}
						</Button>
					</form>
				</LocalizationProvider>
			}
		</>
}

const InputMaskComponent = React.forwardRef((props, inputRef) => {
	return <InputMask mask="+1 (___) ___-____" 
		replacement="_" 
		{...props} />
})
